import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../config/api';
import EventWindowChart from './EventWindowChart';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReportChatbot from './ReportChatbot';
import SeoHead from '../seo/SeoHead';
import { reportMeta } from '../seo/metadata';
import { reportArticleSchema, breadcrumbSchema } from '../seo/structuredData';
import { trackReportView } from '../seo/analytics';
import { buildActivistPath } from '../seo/siteConfig';

const ReportDetails = () => {
  const { activist, slug } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const initialDate = urlParams.get('date') || '';
  const initialTicker = urlParams.get('ticker') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // DEBUG: Enable/disable specific API calls to isolate the issue
  const ENABLE_REPORT_API = true;  // /api/reports endpoint

  useEffect(() => {
    const fetchData = async () => {
      if (!ENABLE_REPORT_API) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await apiClient.get(`/api/reports/${activist}/${slug}`);
        setData(res.data);
      } catch (e) {
        console.error('Report API error:', e);
        setError('Failed to load report details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activist, slug, ENABLE_REPORT_API]);


  const decodedTitle = useMemo(() => decodeURIComponent(slug || ''), [slug]);
  const decodedActivist = useMemo(() => decodeURIComponent(activist || ''), [activist]);

  useEffect(() => {
    if (!data || loading) return;
    trackReportView({
      activist: decodedActivist,
      reportTitle: decodedTitle,
      targetCompany: initialTicker,
      ticker: initialTicker,
    });
  }, [data, loading, decodedActivist, decodedTitle, initialTicker]);

  const seo = reportMeta({
    activist: decodedActivist,
    reportTitle: decodedTitle,
    targetCompany: decodedTitle,
    ticker: initialTicker,
    publicationDate: initialDate,
  });

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      alert('Summary copied');
    } catch (_) {
      /* no-op */
    }
  };

  const formatTextForMarkdown = (raw) => {
    if (!raw) return '';
    let t = String(raw).replace(/\r\n/g, '\n');
    // Remove visual separators
    t = t.replace(/^=+\s*$/gmi, '');
    // Normalize targeted headings
    t = t.replace(/^\s*SHORT SELLER REPORT ANALYSIS\s*:?\s*$/gmi, '# SHORT SELLER REPORT ANALYSIS');
    t = t.replace(/^\s*EXECUTIVE SUMMARY\s*:?\s*$/gmi, '## EXECUTIVE SUMMARY');
    t = t.replace(/^\s*DETAILED ALLEGATIONS AND SOURCES\s*:?\s*$/gmi, '## DETAILED ALLEGATIONS AND SOURCES');
    t = t.replace(/^\s*Key Insights\s*:?\s*$/gmi, '## Key Insights');
    // Drop trailing sections we don't want
    t = t.replace(/^\s*Disclaimer Note:\s*[\s\S]*$/gmi, '');
    t = t.replace(/^\s*ANALYSIS NOTES:\s*[\s\S]*$/gmi, '');
    return t.trim();
  };


  if (loading) {
    return (
      <div className="p-6">
        <SeoHead {...seo} />
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-1/3"></div>
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <SeoHead {...seo} />
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SeoHead
        {...seo}
        jsonLd={[
          reportArticleSchema({
            activist: decodedActivist,
            reportTitle: decodedTitle,
            targetCompany: decodedTitle,
            ticker: initialTicker,
            publicationDate: initialDate,
            externalUrl: data?.pdfUrl,
          }),
          breadcrumbSchema([
            { name: 'Phalanx', path: '/' },
            { name: 'Reports', path: '/reports' },
            { name: decodedActivist, path: buildActivistPath(decodedActivist) },
            { name: decodedTitle, path: `/report/${encodeURIComponent(decodedActivist)}/${encodeURIComponent(decodedTitle)}` },
          ]),
        ]}
      />
      {/* DEBUG Indicator */}
      <div className="bg-yellow-100 border-b-2 border-yellow-400 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-sm font-bold text-yellow-900 mb-1">🔧 DEBUG MODE - API Testing</h3>
          <div className="flex gap-4 text-xs text-yellow-800">
            <span className={ENABLE_REPORT_API ? "text-green-700 font-semibold" : "text-red-700"}>
              Report API: {ENABLE_REPORT_API ? '✓ ENABLED' : '✗ DISABLED'}
            </span>
          </div>
          <p className="text-xs text-yellow-700 mt-1">Check browser console for detailed API logs</p>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <Link
                to={buildActivistPath(decodedActivist)}
                className="text-xs uppercase tracking-wider opacity-90 mb-1 block hover:opacity-100"
              >
                {decodedActivist}
              </Link>
              <h1 className="text-2xl md:text-3xl font-bold leading-snug">{decodedTitle}</h1>
              <div className="mt-3 inline-flex items-center space-x-2 text-xs bg-white/10 px-3 py-1 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full"></span>
                <span>Report Details</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/reports" className="bg-white/90 text-gray-900 hover:bg-white px-3 py-2 rounded-md text-sm font-medium">Back to Reports</Link>
              {data?.pdfUrl && (
                <a href={data.pdfUrl} target="_blank" rel="noopener noreferrer" className="bg-black/30 hover:bg-black/40 px-3 py-2 rounded-md text-sm font-medium border border-white/30">Open PDF</a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left (Market snapshot + Summary) */}
          <div className="order-2 lg:order-1 lg:col-span-2 space-y-6">
            {/* Replaces the old Market Snapshot card, which rendered "No bars"
                unconditionally: it read /api/market/:ticker/bars behind
                ENABLE_MARKET_BARS_API, and that flag has been false. Served by
                the price pipeline instead, and indexed so the shape is
                comparable across every report in the corpus. */}
            {(initialTicker && initialDate) && (
              <div className="card">
                <EventWindowChart ticker={initialTicker} date={initialDate} />
              </div>
            )}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Summary</h2>
                {data?.summary && (
                  <button onClick={() => copyToClipboard(data.summary)} className="text-sm text-blue-600 hover:text-blue-800">Copy</button>
                )}
              </div>
              {data?.summary ? (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{formatTextForMarkdown(data.summary)}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-sm text-gray-600">No summary available</div>
              )}
            </div>
          </div>

          {/* Right column */
          }
          <div className="order-1 lg:order-2 space-y-6">

            <div className="card">
              <h2 className="text-lg font-semibold mb-3">Original Report</h2>
              {(data?.pdfUrl || data?.htmlUrl) ? (
                <div className="space-y-3">
                  {data?.pdfUrl && (
                    <a href={data.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Open PDF in new tab</a>
                  )}
                  {data?.htmlUrl && (
                    <a href={data.htmlUrl} target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Open HTML in new tab</a>
                  )}
                  <div className="text-xs text-gray-500">Temporary link. Expires in about {Math.round((Number(process.env.REACT_APP_REPORT_SIGNED_URL_TTL||3600))/60)} minutes.</div>
                  <div className="border rounded h-96 overflow-hidden bg-gray-50">
                    {/* Prefer direct S3 signed URL for embed; fallback to proxied stream. Handle HTML too. */}
                    <object data={data.pdfUrl || data.htmlUrl}
                            type={data?.pdfUrl ? 'application/pdf' : 'text/html'}
                            className="w-full h-full">
                      <iframe title="report-pdf"
                              src={`/api/reports/${activist}/${slug}/file`}
                              className="w-full h-full" />
                    </object>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">PDF not available</div>
              )}
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Report Meta</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="text-gray-500">Activist:</span> {decodedActivist}</div>
                <div><span className="text-gray-500">Title:</span> {decodedTitle}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Report Chatbot */}
      {data && (
        <ReportChatbot 
          reportData={{
            ...data,
            ticker: initialTicker,
            date: initialDate
          }}
          activist={activist}
          reportTitle={slug}
        />
      )}
    </div>
  );
};

export default ReportDetails;


